// GarmentDirectUpload — no-form uploader for a Marvelous Designer garment.
//
// ENG-1108: dropping a .glb / .gltf that the byte sniff classified as a garment
// (gltfKind.js) uploads it immediately with sensible defaults — name from the
// filename stem, folder from the drop target. There is nothing to fill in: the
// importer measures everything else off the mesh when the character loads it.
//
// Modelled on EnvironmentDirectUpload, including the XHR progress bar, because
// a garment is the same shape of problem: a multi-megabyte binary where a
// frozen modal reads as a hang. On error the modal stays up with the message
// rather than closing (loud failure, per the CLAUDE.md UX rules).

import React, { useEffect, useRef, useState } from 'react'
import WizardShell, { tokens } from './WizardShell'
import { CustomAssetManager } from '../../../lib/CustomAssetManager'
import { assetRegistry } from '../../../lib/assetRegistry'

const FILL = 'var(--m-accent)'
const FILL_DIM = 'color-mix(in srgb, var(--m-accent) 55%, var(--m-bg))'

function baseName(file) {
  return (file?.name ?? '').replace(/\.[^.]+$/, '')
}

function fmtSize(bytes) {
  if (bytes == null || bytes === 0) return '—'
  const units = ['B', 'KB', 'MB', 'GB']
  let n = Number(bytes), i = 0
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++ }
  return `${n.toFixed(n >= 10 ? 0 : 1)} ${units[i]}`
}

export default function GarmentDirectUpload({ file, path = [], onDone, onCancel }) {
  const [error, setError] = useState(null)
  const [progress, setProgress] = useState({ fraction: 0, loaded: 0, total: file?.size ?? 0 })
  const [finalizing, setFinalizing] = useState(false)
  const fired = useRef(false)

  useEffect(() => {
    if (!file || fired.current) return
    fired.current = true
    const folderPath = path.length ? '/' + path.join('/') : '/garments'
    CustomAssetManager.uploadGarmentAsset(file, {
      name: baseName(file),
      folderPath,
      onProgress: (p) => {
        setProgress(p)
        if (p.fraction >= 1) setFinalizing(true)
      },
    })
      .then(row => {
        assetRegistry.setPath(`garment:${row.id}`, path)
        onDone?.(row)
      })
      .catch(e => setError(String(e?.message ?? e)))
  }, [file, path, onDone])

  const pct = progress.fraction >= 0 ? Math.round(progress.fraction * 100) : null

  return (
    <WizardShell
      title={error ? 'Garment upload failed' : (finalizing ? 'Finalizing upload…' : 'Uploading garment…')}
      subtitle={file?.name}
      onCancel={onCancel}
      onSubmit={onCancel}
      submitDisabled={!error}
      submitLabel={error ? 'Close' : (finalizing ? 'Finalizing…' : (pct != null ? `${pct}%` : 'Uploading…'))}
      error={error}
    >
      {!error && (
        <div style={{ padding: '8px 0 4px' }}>
          <div style={{
            width: '100%', height: 10,
            background: tokens.bgInset,
            border: `1px solid ${tokens.border}`,
            borderRadius: 4,
            overflow: 'hidden',
            position: 'relative',
          }}>
            {progress.fraction >= 0 ? (
              <div style={{
                width: `${Math.max(2, (progress.fraction || 0) * 100)}%`,
                height: '100%',
                background: finalizing
                  ? `linear-gradient(90deg, ${FILL_DIM}, ${FILL}, ${FILL_DIM})`
                  : `linear-gradient(90deg, ${FILL_DIM}, ${FILL})`,
                backgroundSize: finalizing ? '200% 100%' : 'auto',
                animation: finalizing ? 'aos-garment-upload-pulse 1.4s linear infinite' : 'none',
                transition: 'width 0.15s linear',
              }} />
            ) : (
              <div style={{
                position: 'absolute', inset: 0,
                background: `linear-gradient(90deg, transparent 0%, ${FILL} 50%, transparent 100%)`,
                backgroundSize: '40% 100%',
                animation: 'aos-garment-upload-stripe 1.2s linear infinite',
              }} />
            )}
          </div>

          <div style={{
            marginTop: 8, color: tokens.dim, fontSize: 11,
            display: 'flex', justifyContent: 'space-between', gap: 8,
          }}>
            <span>{finalizing ? 'Server writing to storage…' : (pct != null ? `${pct}%` : 'uploading…')}</span>
            <span>
              {progress.total > 0
                ? `${fmtSize(progress.loaded)} / ${fmtSize(progress.total)}`
                : (progress.loaded > 0 ? fmtSize(progress.loaded) : fmtSize(file?.size))}
            </span>
          </div>
        </div>
      )}
      <style>{`
        @keyframes aos-garment-upload-stripe { from { background-position: -40% 0; } to { background-position: 140% 0; } }
        @keyframes aos-garment-upload-pulse  { from { background-position: 0 0; } to { background-position: 200% 0; } }
      `}</style>
    </WizardShell>
  )
}
